import { Link, useNavigate } from '@tanstack/react-router';
import { Clock } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { CodigoDeSeisDigitos } from '@/componentes/CodigoDeSeisDigitos';
import { codigoCompleto } from '@/componentes/codigo-de-seis-digitos';
import { limparRetornos } from '@/lib/retorno';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoTexto } from '@/componentes/Campo';
import { CoMarcaLogin } from '@/componentes/Marca';
import { HistoriaNaMesa } from '@/historias/mesa';
import { primeiraMaiuscula } from '@/lib/texto';
import {
  etapaDoCodigoVenceu,
  explicarFalhaDeEntrada,
  useConfirmarCodigo,
  useEntrar,
  useSessao,
} from '@/modulos/sessao/consultas';
import {
  buscarNoNavegador,
  lembrarDepoisDeEntrar,
  lerEscolhasDaEntrada,
  preencherComOGuardado,
} from '@/modulos/sessao/lembranca';
import { sessaoEmMemoria } from '@/modulos/sessao/memoria';

/**
 * A entrada do sistema. Só isso, e é esse o assunto do arquivo.
 *
 * ## O que saiu daqui, e por quê
 *
 * Esta tela carregava, além do formulário, dois documentos: a **proposta de
 * permissão** dos nove perfis e o **escopo além do papel**. Eles estavam aqui
 * por um motivo que fazia sentido na época — a semeadura criava pessoa para
 * cinco dos nove perfis, então não havia como *ser* um planejador de manutenção
 * ou alguém de compras, e o documento no login fazia o papel de substituto.
 *
 * Quando todo perfil ganhou conta, o substituto perdeu a razão, e o dono
 * apontou: *"por que proposta de permissão não saiu do login sendo que tem um
 * e-mail para cada persona?"*. Três coisas estavam erradas em manter aqui:
 *
 *  1. Ficava **visível sem sessão** — o desenho de permissões do sistema
 *     inteiro, legível por quem apenas alcançasse o endereço.
 *  2. O público era **quem decide a matriz**, e essa decisão acontece dentro do
 *     sistema, ao lado de papel, permissão e alçada.
 *  3. Tela de entrada é para **entrar**, e o documento era maior que o
 *     formulário.
 *
 * Tudo aquilo agora vive em `PaginaPerfil.tsx`, mostrando **o perfil de quem
 * está logado** em vez de um catálogo com seletor. A diferença não é de
 * arrumação: persona escolhida num seletor foi exatamente o que esta tela
 * deixou de ser quando a autenticação passou a existir. O papel vem da
 * CREDENCIAL agora.
 *
 * ## O que esta tela continua NÃO fazendo
 *
 *  1. **Não há recuperação de senha, primeiro acesso nem política de senha.**
 *     Quem perde a senha pede a quem administra o sistema — e é isso que a
 *     recusa do servidor já diz, com essas palavras, quando o login falha. Um
 *     bloco de avisos repetindo a mesma frase abaixo do formulário só faria a
 *     tela crescer. Está em `docs/PENDENCIAS.md`.
 *  2. **Entrar não decide o que se pode fazer.** O papel estreita o menu e não
 *     barra nada: nenhuma rota do servidor confere permissão. Quem entra lê essa
 *     ressalva onde ela é útil — no próprio perfil, dentro do sistema.
 *
 * ## A co-marca
 *
 * O manual (p.24) diz que o topo do sistema e o login são as duas únicas telas
 * que levam as duas marcas. Aqui elas ficam lado a lado, na construção da
 * p.24: Contrato dominante, Atenas assinando como fornecedora.
 */
export function PaginaLogin() {
  const navegar = useNavigate();
  const sessao = useSessao();
  const entrar = useEntrar();
  const confirmar = useConfirmarCodigo();

  /* O QUE O APARELHO LEMBRA (pedido do dono, 31/10/2026): as duas escolhas e,
     com "Lembrar meus dados" ligado, o ultimo e-mail. A senha nunca: ela fica
     no gerenciador de senhas do navegador (`sessao/lembranca.ts`). */
  const [inicio] = useState(lerEscolhasDaEntrada);
  const [email, setEmail] = useState(inicio.email);
  const [senha, setSenha] = useState('');
  const [lembrar, setLembrar] = useState(inicio.lembrar);
  const [manter, setManter] = useState(inicio.manter);
  /* A ETAPA DO CODIGO (0085): a senha conferiu e a pessoa tem o segundo
     fator. O identificador vale cinco minutos e so serve para mandar o codigo. */
  const [pendente, setPendente] = useState<string | null>(null);
  const [codigo, setCodigo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  /* R06 (p.68): "Usar código de recuperação" troca os seis campos por um
     campo de texto em mono; o código errado deixa os seis vermelhos e vazios;
     a etapa vencida (5 minutos) mostra "Voltar para a entrada". */
  const [recuperacao, setRecuperacao] = useState(false);
  const [errou, setErrou] = useState(false);
  const [venceu, setVenceu] = useState(false);

  /* A senha guardada no gerenciador do navegador PREENCHE os campos, onde a
     API existe (Chrome, Edge). Nunca entra sozinha: o botao e da pessoa. E so
     preenche o que a pessoa ainda nao digitou (`preencherComOGuardado`). */
  const digitado = useRef({ email: inicio.email, senha: '' });
  useEffect(() => {
    digitado.current = { email, senha };
  });
  useEffect(() => {
    if (!inicio.lembrar) return undefined;
    let viva = true;
    void buscarNoNavegador().then((guardado) => {
      const preenchido = preencherComOGuardado(digitado.current, guardado);
      if (!viva || preenchido === null) return;
      setEmail(preenchido.email);
      setSenha(preenchido.senha);
    });
    return () => {
      viva = false;
    };
  }, [inicio.lembrar]);

  useEffect(() => {
    if (pendente === null) return;
    const relogio = setTimeout(() => {
      setVenceu(true);
    }, MINUTOS_DA_ETAPA * 60_000);
    return () => {
      clearTimeout(relogio);
    };
  }, [pendente]);

  const podeEnviar = email.trim().length > 0 && senha.length > 0 && !entrar.isPending;
  const recusa = entrar.isError ? explicarFalhaDeEntrada(entrar.error) : null;
  const recusaDoCodigo = confirmar.isError ? explicarFalhaDeEntrada(confirmar.error) : null;

  function seguir() {
    /* LEMBRAR ANTES DE SAIR DA TELA: as escolhas, o e-mail e (com "Lembrar"
       ligado) a senha entregue ao gerenciador do navegador. O nome vem da
       sessao que a entrada acabou de carregar.

       E A SENHA NAO E MAIS APAGADA AQUI (31/10/2026). Apagar o campo antes de
       a tela sair fazia o formulario ser desmontado com a senha VAZIA, e o
       navegador que confere o formulario na saida (o Firefox e o Safari, nas
       telas que entram por `fetch`) nao tinha o que oferecer guardar. A senha
       some com a tela, que e desmontada pela navegacao logo abaixo. */
    void lembrarDepoisDeEntrar({ email, senha, lembrar, manter, nome: sessaoEmMemoria()?.nome ?? null });
    /* O que falhou ANTES de entrar (a lista que tentou carregar sem
       sessão) não diz nada depois: sai junto com a tela de entrada. */
    limparRetornos();
    /* A frota é a primeira pergunta de todo dia, e é para onde a raiz
       do sistema já redireciona. Mandar para o "primeiro módulo do
       perfil" seria usar a proposta — que não é decisão do dono — como
       se fosse regra. */
    void navegar({ to: '/equipamentos', search: {} });
  }

  function voltarASenha(motivo: string | null) {
    setPendente(null);
    setCodigo('');
    setSenha('');
    setRecuperacao(false);
    setErrou(false);
    setVenceu(false);
    confirmar.reset();
    setAviso(motivo);
  }

  const codigoPronto = recuperacao ? codigo.trim() !== '' : codigoCompleto(codigo);

  function enviarCodigo(evento: React.FormEvent) {
    evento.preventDefault();
    if (pendente === null || !codigoPronto || confirmar.isPending) return;
    confirmar.mutate(
      { pendente, codigo: codigo.trim(), manterConectado: manter },
      {
        onSuccess: seguir,
        onError: (erro) => {
          if (etapaDoCodigoVenceu(erro)) {
            setVenceu(true);
            return;
          }
          /* Código errado: os seis campos ficam vermelhos e esvaziam (p.68). */
          setErrou(true);
          setCodigo('');
        },
      },
    );
  }

  function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!podeEnviar) return;
    setAviso(null);
    entrar.mutate(
      { email: email.trim(), senha, manterConectado: manter },
      {
        onSuccess: (resultado) => {
          if (resultado.entrou) {
            seguir();
            return;
          }
          setPendente(resultado.pendente);
        },
      },
    );
  }

  return (
    /* 688 px, e o número sai da MESMA aritmética que justificava os 1120 de
       antes: 560 da coluna de formulário (p.16) + 64 das duas margens de 32
       (`--layout-margem-xl`) + 64 de folga. O que desapareceu da conta foram
       os 400 do painel de documento e os 32 do vão entre os dois — porque o
       painel saiu desta tela. O manual não tem medida para tela sem navegação
       lateral; isso continua PENDENTE nele, e continua derivado do que ele já
       define.

       A MARGEM É A DA R01 (p.28): 32 acima de 1280, 24 entre 768 e 1279, 16
       abaixo. Antes era `p-12` (48 px) em toda largura, e a 390 px isso comia
       um quarto da tela. Esta tela está FORA da casca, então ela não herda a
       margem de `pagina` e precisa declará-la. */
    <div className="h-full overflow-auto bg-tela">
      <div className="mx-auto flex max-w-[688px] flex-col gap-8 p-[var(--margem-xl)] max-[1280px]:p-[var(--margem-m)] max-[768px]:p-[var(--margem-xs)]">
        <CoMarcaLogin />

        <CabecalhoPagina
          sobretitulo="CT Frotas"
          titulo="Entrar"
          subtitulo="Use o e-mail e a senha cadastrados para você"
        />

        {pendente !== null && venceu ? (
          /* A etapa venceu (p.68): o aviso âmbar e a saída, no mesmo cartão. */
          <Bloco titulo="Digite o código">
            <p className="flex items-start gap-2 rounded-controle bg-atencao-fundo p-3 txt-apoio text-atencao-texto">
              <Clock aria-hidden="true" className="mt-0.5 size-[var(--icone-controle)] shrink-0 text-atencao" />
              {`A etapa venceu depois de ${String(MINUTOS_DA_ETAPA)} minutos. Entre de novo com e-mail e senha.`}
            </p>
            <Botao
              nivel="primario"
              className="w-full"
              onClick={() => {
                voltarASenha(null);
              }}
            >
              Voltar para a entrada
            </Botao>
          </Bloco>
        ) : pendente !== null ? (
          /* A etapa do código ocupa o lugar do formulário de entrada, no mesmo cartão (p.68). */
          <Bloco titulo="Digite o código">
            <form className="flex flex-col gap-4" onSubmit={enviarCodigo}>
              <p className="txt-apoio max-w-[72ch] text-pretty text-tinta-secundaria">
                {recuperacao
                  ? 'Digite um dos códigos de recuperação que você guardou ao ativar. Cada um vale uma vez.'
                  : 'Abra o aplicativo autenticador e digite os seis dígitos da Contrato.'}
              </p>
              {recuperacao ? (
                <div className="[&_input]:font-[family-name:var(--fonte-codigo)]">
                  <CampoTexto
                    rotulo="Código de recuperação"
                    valor={codigo}
                    autoComplete="off"
                    maxLength={24}
                    placeholder="8K2F-QX7P"
                    aoMudar={(v) => {
                      confirmar.reset();
                      setCodigo(v);
                    }}
                  />
                </div>
              ) : (
                <CodigoDeSeisDigitos
                  rotulo="Código do aplicativo"
                  valor={codigo}
                  erro={errou}
                  focarAoAbrir
                  aoMudar={(v) => {
                    setErrou(false);
                    confirmar.reset();
                    setCodigo(v);
                  }}
                />
              )}
              {recusaDoCodigo === null ? null : (
                <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
                  <span className="txt-corpo text-tinta">{primeiraMaiuscula(recusaDoCodigo.erro)}</span>
                  <span className="txt-apoio text-tinta-secundaria">{recusaDoCodigo.comoResolver}</span>
                </div>
              )}
              <Botao nivel="primario" type="submit" className="w-full" tamanho="g" disabled={!codigoPronto} carregando={confirmar.isPending}>
                {confirmar.isPending ? 'Conferindo…' : 'Entrar'}
              </Botao>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Botao
                  nivel="fantasma"
                  onClick={() => {
                    setRecuperacao((r) => !r);
                    setCodigo('');
                    setErrou(false);
                    confirmar.reset();
                  }}
                >
                  {recuperacao ? 'Usar o aplicativo' : 'Usar código de recuperação'}
                </Botao>
                <Botao
                  nivel="fantasma"
                  onClick={() => {
                    voltarASenha(null);
                  }}
                >
                  Voltar
                </Botao>
              </div>
            </form>
          </Bloco>
        ) : (
        <Bloco titulo="Entrar no sistema">
          {/* Formulário de verdade: `<form>` para o Enter enviar, e
              `autoComplete` para o gerenciador de senhas do navegador
              reconhecer os dois campos. Um login que não deixa a senha ser
              guardada empurra todo mundo para senha curta. */}
          {/* `method="post"`: e o envio que os gerenciadores de senha esperam
              de um formulario de entrada. O `onSubmit` cancela a ida de
              verdade e entra por `fetch`, mas o envio do `<form>` (Enter ou o
              botao `submit`) e o sinal que eles leem. */}
          <form method="post" className="flex flex-col gap-4" onSubmit={enviar}>
            {/* Os dois tetos sao os do esquema do servidor (320 e 200).
                Sem eles, um `ctrl+v` de texto longo virava uma ida ao
                servidor cuja resposta a tela mostrava como titulo do
                alerta — e o `.max()` do Zod nao tinha mensagem propria,
                entao a frase que aparecia era da biblioteca, em ingles. */}
            <CampoTexto
              rotulo="E-mail"
              valor={email}
              tipo="email"
              autoComplete="username"
              nome="email"
              idFixo="entrar-email"
              maxLength={320}
              aoMudar={setEmail}
            />
            <CampoTexto
              rotulo="Senha"
              valor={senha}
              tipo="senha"
              autoComplete="current-password"
              nome="password"
              idFixo="entrar-senha"
              maxLength={200}
              aoMudar={setSenha}
            />

            {/* As duas escolhas da entrada, as mesmas do celular (M01). Caixa
                de selecao, e nao chave (p.24): valem quando a pessoa entra, e
                nao no clique. */}
            <Escolha
              id="entrar-manter"
              rotulo="Manter conectado"
              ajuda="Você continua no sistema neste computador até clicar em Sair. Desmarcado, a sessão termina quando o navegador fecha."
              marcada={manter}
              aoMudar={setManter}
            />
            <Escolha
              id="entrar-lembrar"
              rotulo="Lembrar meus dados neste aparelho"
              ajuda="O e-mail fica preenchido na próxima entrada, e o navegador oferece guardar a senha no gerenciador de senhas dele. O sistema nunca guarda a senha."
              marcada={lembrar}
              aoMudar={setLembrar}
            />

            {/* A RECUSA É A DO SERVIDOR, palavra por palavra. Ela não
                distingue senha errada de e-mail que não existe — de
                propósito, e o servidor gasta o mesmo tempo nos dois casos
                para o relógio não responder o que a frase calou.

                E SÃO DUAS FRASES, não uma. O 429 do freio de tentativa diz
                quanto esperar, e isso não fura a regra acima: o freio conta
                pelo e-mail DIGITADO, exista conta para ele ou não, então
                "muitas tentativas" não revela se a conta existe. Unificar as
                duas aqui deixaria quem acertou a senha lendo "confira o
                e-mail e a senha" enquanto a saída dele era esperar. */}
            {aviso === null ? null : (
              <p role="status" className="txt-apoio text-tinta-secundaria">
                {aviso}
              </p>
            )}

            {recusa === null ? null : (
              <div
                role="alert"
                className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4"
              >
                <span className="txt-corpo text-tinta">{primeiraMaiuscula(recusa.erro)}</span>
                <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
              </div>
            )}

            {sessao.data === null || sessao.data === undefined ? null : (
              <p className="txt-apoio text-tinta-secundaria">
                Você já está no sistema como {sessao.data.nome}, da {sessao.data.empresa}. Entrar
                com outro e-mail encerra a sessão deste navegador e abre outra.
              </p>
            )}

            <div className="flex items-center justify-end">
              <HistoriaNaMesa codigo="BTN-ACS-01" className="flex">
                <Botao nivel="primario" type="submit" disabled={!podeEnviar}>
                  {/* Verbo + objeto (p.15): "Entrar" sozinho e da familia de
                      "OK" e "Enviar", que o manual proibe. */}
                  {entrar.isPending ? 'Entrando…' : 'Entrar no sistema'}
                </Botao>
              </HistoriaNaMesa>
            </div>
          </form>
        </Bloco>
        )}

        {/* A ADMINISTRACAO DA PLATAFORMA (decisao 21) tem entrada propria, com
            senha propria: quem administra o SISTEMA, acima das empresas, nao
            entra por aqui. O mesmo e-mail pode ter as duas contas, e uma nao
            abre a outra. */}
        {/* LINK DE TEXTO, e nao botao: com `botao-terciario` ele saia em
            laranja 600 sobre o canvas, que mede 4,39:1 — abaixo dos 4,5:1 da
            regra 7 —, e cinco palavras com cara de botao competiam com o
            laranja de "Entrar no sistema", que e a acao desta tela. */}
        <p className="txt-apoio text-tinta-secundaria">
          Administra o sistema, acima das empresas?{' '}
          <Link to="/plataforma" className="text-tinta-secundaria underline">
            Entrar na administração
          </Link>
        </p>
      </div>
    </div>
  );
}

/** Quanto vale a etapa do código depois da senha (0085: `interval '5 minutes'`). */
const MINUTOS_DA_ETAPA = 5;

/** Uma escolha da entrada: caixa de selecao com o rotulo e a consequencia escrita. */
function Escolha({
  id,
  rotulo,
  ajuda,
  marcada,
  aoMudar,
}: {
  readonly id: string;
  readonly rotulo: string;
  readonly ajuda: string;
  readonly marcada: boolean;
  readonly aoMudar: (marcada: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3">
      <input
        id={id}
        type="checkbox"
        className="mt-1 size-4 shrink-0 accent-[var(--acao-primaria)]"
        checked={marcada}
        onChange={(evento) => {
          aoMudar(evento.target.checked);
        }}
      />
      <span className="flex flex-col gap-1">
        <span className="txt-rotulo-campo">{rotulo}</span>
        <span className="txt-ajuda">{ajuda}</span>
      </span>
    </label>
  );
}

function Bloco({ titulo, children }: { readonly titulo: string; readonly children: ReactNode }) {
  return (
    <section className="cartao flex flex-col gap-4 p-6">
      <h2 className="txt-secao text-tinta">{titulo}</h2>
      {children}
    </section>
  );
}
